// /src/hooks/useInstallPrompt.js
// Single source of truth for PWA install state, shared by every install surface
// (the mobile InstallAppPrompt pill and the desktop ProfileMenu button).
//
// Why a hook: beforeinstallprompt can fire BEFORE React mounts. public/index.html
// captures it early and stashes the event on window.deferredInstallPrompt (it does
// NOT dispatch a custom event). So each consumer must do BOTH: read that global on
// mount (early-fire case) AND attach its own listener (later-fire case). This hook
// does that once, so components no longer duplicate — or diverge on — that logic.

import { useState, useEffect, useCallback } from 'react';

// iOS never fires beforeinstallprompt — detect it so callers can show manual
// Add-to-Home-Screen instructions instead of a (never-available) install button.
const detectIOS = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

// Already running as an installed app — every install surface should hide.
const detectStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

/**
 * useInstallPrompt - PWA install availability + action.
 *
 * @returns {Object} {
 *   canInstall,    // boolean — true ONLY when a real deferred prompt is held
 *   install,       // async () => 'accepted' | 'dismissed' | null — fires native dialog
 *   isIOS,         // boolean — iOS (needs manual Add-to-Home-Screen instructions)
 *   isStandalone   // boolean — already installed / launched standalone
 * }
 *
 * Visibility contract for consumers:
 *   isStandalone            -> render nothing (already installed)
 *   canInstall              -> show install button, onClick => install()
 *   !canInstall && isIOS    -> show Add-to-Home-Screen instructions
 *   !canInstall && !isIOS   -> render nothing (no dead button)
 */
export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const isIOS = detectIOS();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1. Early-fire case: index.html may have captured the event before React mounted.
    if (window.deferredInstallPrompt) {
      setDeferredPrompt(window.deferredInstallPrompt);
    }

    // 2. Later-fire case: attach our own listener (fires alongside index.html's writer).
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e; // keep the global in sync for any late consumer
      setDeferredPrompt(e);
    };

    // 3. Installed → drop the (single-use) prompt so canInstall flips false and hides.
    const handleAppInstalled = () => {
      window.deferredInstallPrompt = null;
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    // 4. React if the user installs while viewing (display-mode flips to standalone).
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => setIsStandalone(e.matches);

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    standaloneQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      standaloneQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const install = useCallback(async () => {
    const prompt = deferredPrompt || window.deferredInstallPrompt;
    if (!prompt) return null;

    prompt.prompt();
    const { outcome } = await prompt.userChoice;

    // A deferred prompt can only be used once — clear it regardless of outcome.
    window.deferredInstallPrompt = null;
    setDeferredPrompt(null);

    return outcome;
  }, [deferredPrompt]);

  return {
    canInstall: !!deferredPrompt && !isStandalone,
    install,
    isIOS,
    isStandalone
  };
};

export default useInstallPrompt;
